import React, { useEffect } from 'react';

import { Box, Divider, FormControlLabel, Skeleton, Typography } from '@mui/material';

import { useTranslation } from 'react-i18next';

import { useDispatch, useSelector } from 'react-redux';

import { useInjectReducer, useInjectSaga } from 'redux-injectors';

import { RootState } from 'types';

import { convertDateToPST } from 'app/helpers/dateHelper';

import ClockIcon from 'app/assets/images/IconImages/clockIcon.svg';

import { LayoutGrid, LayoutGridItem } from 'app/layout/LayoutGrid';

import { CandidateContainer } from '../CandidateTabPanel/SummaryTab/Common/CandidateContainer';

import { CandidateSubHeader } from '../CandidateTabPanel/SummaryTab/Common/CandidateSubHeader';

import { useStyles } from '../CandidateTabPanel/SummaryTab/Cards/Card Design/utils';

import {

  candidateEventSubmissionControlActions,

  candidateEventSubmissionControlReducer,

  candidateEventSubmissionControlSliceKey,

} from './store/candidateEventSubmissionControl.redux';

import { candidateEventSubmissionControlSaga } from './store/candidateEventSubmissionControl.saga';
import { SubmissionPausedToggleSwitch } from './SubmissionPausedToggleSwitch';

import {

  selectCandidateEventSubmissionControlsForTraveler,

  selectCandidateEventSubmissionControlListStatusForTraveler,

  selectCandidateEventSubmissionControlSavingAffiliationIds,

} from './store/candidateEventSubmissionControl.selector';



const LABOR_DISRUPTION_BRAND_ID = 14;



export interface SubmissionPausedSummaryCardProps {

  travelerId: number;

  brandId?: number;

}



export const SubmissionPausedSummaryCard = ({ travelerId, brandId }: SubmissionPausedSummaryCardProps) => {

  useInjectReducer({ key: candidateEventSubmissionControlSliceKey, reducer: candidateEventSubmissionControlReducer });

  useInjectSaga({ key: candidateEventSubmissionControlSliceKey, saga: candidateEventSubmissionControlSaga });



  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { classes } = useStyles();

  const items = useSelector((state: RootState) =>
    selectCandidateEventSubmissionControlsForTraveler(state, travelerId),
  );

  const listStatus = useSelector((state: RootState) =>
    selectCandidateEventSubmissionControlListStatusForTraveler(state, travelerId),
  );

  const savingAffiliationIds = useSelector(selectCandidateEventSubmissionControlSavingAffiliationIds);

  const isLaborDisruptionBrand = brandId === LABOR_DISRUPTION_BRAND_ID;
  const canFetch = travelerId > 0;

  useEffect(() => {
    if (!canFetch || !isLaborDisruptionBrand) {
      dispatch(candidateEventSubmissionControlActions.resetSubmissionControl());
      return;
    }

    dispatch(candidateEventSubmissionControlActions.fetchSubmissionControlsList({ travelerId }));

    return () => {
      dispatch(candidateEventSubmissionControlActions.resetSubmissionControl());
    };
  }, [canFetch, dispatch, isLaborDisruptionBrand, travelerId]);

  const shouldShowCard = canFetch && isLaborDisruptionBrand && listStatus !== 'error';

  if (!shouldShowCard) {
    return null;
  }



  const handleToggle = (affiliationId: number, checked: boolean) => {

    dispatch(

      candidateEventSubmissionControlActions.updateSubmissionPause({

        travelerId,

        affiliationId,

        isSubmissionPaused: checked,

      }),

    );

  };



  const getEventDisplayName = (eventName?: string | null) => {

    const trimmedName = eventName?.trim();

    return trimmedName || t('candidate.submissionPaused.eventNameUnknown');

  };



  const renderContent = () => {
    if (listStatus === 'idle' || listStatus === 'loading') {
      return (
        <LayoutGridItem mr={2}>
          <Skeleton />
          <Skeleton />
        </LayoutGridItem>
      );
    }

    if (items.length === 0) {
      return (
        <LayoutGridItem sx={{ paddingLeft: '3px' }}>
          <Typography variant="body2" color="text.secondary">
            {t('candidate.submissionPaused.noEvents')}
          </Typography>
        </LayoutGridItem>
      );
    }

    return items.map((item, index) => {

      const lastUpdatedText =

        item.lastUpdated && item.lastUpdatedByUserName

          ? t('candidate.submissionPaused.lastUpdated', {

              date: convertDateToPST(item.lastUpdated),

              user: item.lastUpdatedByUserName,

            })

          : null;



      return (

        <React.Fragment key={item.affiliationId}>

          {index > 0 && <Divider sx={{ mb: '12px' }} />}

          <CandidateSubHeader title={getEventDisplayName(item.eventName)}>

            <LayoutGrid direction="column" spacing={0} className={classes.content}>

              <LayoutGridItem sx={{ paddingLeft: '3px' }}>

                <FormControlLabel

                  sx={{

                    margin: 0,

                    alignItems: 'center',

                    '& .MuiFormControlLabel-label': {

                      fontSize: '14px',

                      fontWeight: 'normal',

                      color: 'inherit',

                    },

                  }}

                  control={

                    <SubmissionPausedToggleSwitch
                      isSubmissionPaused={!!item.isSubmissionPaused}
                      checked={!!item.isSubmissionPaused}
                      onChange={checked => handleToggle(item.affiliationId, checked)}
                      externallyDisabled={savingAffiliationIds.includes(item.affiliationId)}
                      inputProps={{
                        'aria-label': t('candidate.submissionPaused.toggleLabel'),
                      }}
                    />

                  }

                  label={t('candidate.submissionPaused.toggleLabel')}

                />

              </LayoutGridItem>

              {lastUpdatedText ? (

                <LayoutGridItem sx={{ paddingLeft: '3px', pt: 0.5 }}>

                  <Typography variant="caption" color="text.secondary" display="block">

                    {lastUpdatedText}

                  </Typography>

                </LayoutGridItem>

              ) : null}

            </LayoutGrid>

          </CandidateSubHeader>

        </React.Fragment>

      );

    });

  };



  return (

    <LayoutGridItem style={{ width: '100%' }} pb="12px" data-testid="submission-paused-summary-card">

      <Box className={classes.container}>

        <CandidateContainer

          title={t('candidate.submissionPaused.title')}

          icon={<img src={ClockIcon} alt="Submission Paused" />}

        >

          <LayoutGridItem>{renderContent()}</LayoutGridItem>

        </CandidateContainer>

      </Box>

    </LayoutGridItem>

  );

};